import React, {useState} from 'react';
import {useEnvironment, useExperiments, useTranslation} from '@wix/yoshi-flow-editor';
import {useControllerProps} from '../../../../../../../domain/controllers/ControllerContext';
import {SPECS} from '../../../../../../../domain/specs';
import {InlineAddressForm} from './InlineAddressForm/InlineAddressForm';
import s from './DestinationPicker.scss';
import type {AddressModel} from '../../../../../../../domain/models/Cart/Address.model';
import {loadCountriesData} from './loadCountriesData';
import type {CountriesData} from './loadCountriesData';
import classNames from 'clsx';

type DestinationPickerProps = {
  dataHook: string;
  className?: string;
  hasPartialAddressError?: boolean;
};

export enum DestinationPickerDataHook {
  Button = 'DestinationPicker.Button',
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const DestinationPicker = ({dataHook, className, hasPartialAddressError}: DestinationPickerProps) => {
  const {t} = useTranslation();
  const {language} = useEnvironment();
  const {experiments} = useExperiments();
  const [isInlineFormOpen, setIsInlineFormOpen] = useState(false);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [countriesData, setCountriesData] = useState<CountriesData>(undefined);
  const {
    handleChangeRegion,
    onSelectAddress,
    showErrorToast,
    shouldDisplayDelivery,
    shouldDisplayTax,
    shouldHideCountryPicker,
    enabledDeliveryCountryCodes,
  } = useControllerProps().orderStore;
  const {cartModel, isNonShippableCart} = useControllerProps().cartStore;

  const getChooseRegionTranslationKey = (): string => {
    if (shouldDisplayDelivery && shouldDisplayTax) {
      return 'cart.shipping_destination.shipping_and_tax.choose_region';
    }
    if (shouldDisplayDelivery) {
      return 'cart.shipping_destination.choose_region';
    }
    return 'cart.shipping_destination.tax.choose_region';
  };

  const getDestinationText = (address?: AddressModel): string => {
    if (hasPartialAddressError) {
      return undefined;
    }
    if (address?.countryFullname && address?.subdivisionFullname) {
      return t('cart.estimated_shipping_address', {
        country: address.countryFullname,
        subdivision: address.subdivisionFullname,
      });
    }
    if (address?.countryFullname) {
      return address.countryFullname;
    }
  };

  const getAriaLabel = (address: AddressModel) => {
    const region = getDestinationText(address);
    return region ? t('cart.sr_change_destination', {destinationName: region}) : t(getChooseRegionTranslationKey());
  };

  function getButtonText(address: AddressModel) {
    const region = getDestinationText(address);
    return region ?? t(getChooseRegionTranslationKey());
  }

  const loadCountries = async () => {
    if (isLoadingCountries || countriesData) {
      return;
    }
    setIsLoadingCountries(true);
    try {
      setCountriesData(
        await loadCountriesData(language, isNonShippableCart ? undefined : {enabledDeliveryCountryCodes})
      );
    } catch {
      setIsInlineFormOpen(false);
      void showErrorToast('locale-dataset load failed');
    } finally {
      setIsLoadingCountries(false);
    }
  };

  const handleInlineSubmit = (address: {country: string; subdivision?: string; postalCode?: string}) => {
    void onSelectAddress(address);
    setIsInlineFormOpen(false);
  };

  const handleClick = () => {
    if (experiments.enabled(SPECS.ecomInlineAddressSelectionInCart)) {
      if (!isInlineFormOpen) {
        void loadCountries();
      }
      setIsInlineFormOpen((prev) => !prev);
    } else {
      void handleChangeRegion();
    }
  };

  const {deliveryInfo, paymentInfo} = cartModel;
  const address = isNonShippableCart ? paymentInfo?.address : deliveryInfo?.address;

  return shouldHideCountryPicker ? null : (
    <div
      className={
        experiments.enabled(SPECS.ecomInlineAddressSelectionInCart) ? classNames(s.location, className) : s.locationOld
      }
      data-hook={dataHook}>
      <button
        className={s.countryPickerButton}
        data-hook={DestinationPickerDataHook.Button}
        type="button"
        onClick={handleClick}
        aria-haspopup={true}
        aria-describedby="change-region-description"
        aria-label={getAriaLabel(address)}>
        {getButtonText(address)}
      </button>
      {isInlineFormOpen && (
        <InlineAddressForm
          address={address}
          onSubmit={handleInlineSubmit}
          isLoadingCountries={isLoadingCountries}
          countryData={countriesData}
        />
      )}
    </div>
  );
};
